// shared-types/src/payment/types.ts

import { Currency } from '../common'
import { AccountPaymentMethod } from '../accountPaymentMethod'
import { Order } from '../order'
import { StatusInfo } from '../common/Status'

export enum PaymentStatus {
    PENDING = 'PENDING', // Pendiente
    PREAUTHORIZED = 'PREAUTHORIZED',
    APPROVED = 'APPROVED', // Pago aprobado online
    PAID = 'PAID', // Pago realizado por redes fisicas
    REJECTED = 'REJECTED', // Pago rechazado
    REFUND_IN_PROCESS = 'REFUND_IN_PROCESS', // En proceso de reembolso con la plataforma de pagos
    PARTIAL_REFUND = 'PARTIAL_REFUND', // Pago parcialmente reembolsado
    REFUNDED = 'REFUNDED', // Pago reembolsado
    ERROR = 'ERROR', // Represents an error in porcessing the payment. Ex: a webhook comes without an esternal reference, so we can't match in our system
}

export enum PaymentMethodType {
    BANK_TRANSFER = 'BANK_TRANSFER',
    CREDIT_CARD = 'CREDIT_CARD',
    DEBIT_CARD = 'DEBIT_CARD',
    MERCADOPAGO = 'MERCADOPAGO',
    MERCADOPAGO_MARKETPLACE = 'MERCADOPAGO_MARKETPLACE',
    PHYSICAL = 'PHYSICAL',
    INTERNATIONAL = 'INTERNATIONAL',
    PAYPAL = 'PAYPAL',
    AKUA = 'AKUA',
    CASH = 'CASH',
    OTHER = 'OTHER', // Added for flexibility
}
export interface PaymentConversion {
    fromCurrency: Currency // Moneda de la cuenta/orden (ej: UYU)
    toCurrency: Currency // Moneda del cobro real (ej: USD)
    rate: number // Tasa aplicada (1 Base = X Target)
    originalAmount: number // Monto en moneda de la cuenta
    finalAmount: number // Monto que se envió al procesador
}

export interface Payment {
    id: string
    accountId: string
    orderId: string
    invoiceId?: string
    accountPaymentMethodId?: string
    accountIntegrationId?: string
    referenceCode?: string
    paymentMethodType?: PaymentMethodType
    currency: Currency
    amount: number
    amountReceived: number
    amountRefunded: number
    paidAt?: string | Date
    refundedAt?: string | Date
    status: PaymentStatus
    statusInfo?: StatusInfo
    cardBrand?: string
    cardBrandInfo?: PaymentCardBrand
    cardLast4?: string
    data?: Record<string, any>
    conversion?: PaymentConversion
    metadata?: Record<string, any>
    internalComment?: string
    demo: boolean
    createdAt: string | Date
    updatedAt: string | Date
    deletedAt?: string | Date

    accountPaymentMethod?: Partial<AccountPaymentMethod> | null
    order?: Order

    allowedActions?: {
        canCopyLink: boolean
        canMarkAsPaid: boolean
        canRecapture: boolean
        canRefund: boolean
    }
}

// Provider-level types (entity-agnostic, reusable across projects)
export type PaymentProviderKey =
    | 'MERCADOPAGO'
    | 'MERCADOPAGO_MARKETPLACE'
    | 'PLEXO'
    | 'MANUAL_PAYMENT'
    | 'PAYPAL'
    | 'AKUA'

// TODO: Eventually refactor what is passed to the payment provider constructor, as it most likely just need
export interface PaymentProviderContext {
    data: Record<string, unknown>
}

export interface PaymentProviderInitInput {
    // Arbitrary provider input. Caller builds this from their domain data
    data: Record<string, any>
}

export interface PaymentProviderInitOutput {
    data: Record<string, any>
    status: PaymentStatus
}

export interface PaymentProviderCaptureInput {
    data: Record<string, any>
}

export interface PaymentProviderCaptureOutput {
    data: Record<string, any>
}

export interface PaymentProviderRefundInput {
    amount: number
    data: Record<string, any>
}

export interface PaymentProviderRefundOutput {
    data: Record<string, any>
}

export interface PaymentProviderWebhookResult {
    //TODO: Ideally, if later an order can have multiple payments, the way to link thrid party payments with our own payment entity, should be throu something like a
    // payment session in our system, that would be set as the external reference in the third party payment
    paymentId: string | null
    status: PaymentStatus
    data: Record<string, unknown>
    // TODO: Ideally i think this should live inside the data field, not have separate columns and just let the consumer know what
    // attributes to query to resolve them
    paymentDetails: {
        referenceCode?: string
        method?: string
        last4?: string
    }
}

export interface WebhookPayload {
    // Id of payment provider webhook belongs to, to pass data for processing
    provider: string
    accountId: string
    payload: {
        query: Record<string, unknown>
        body: Record<string, unknown>
        headers: Record<string, unknown>
    }
}

export enum PaymentCardBrandKey {
    mp_account_money = 'mp_account_money',
    master = 'master',
    debmaster = 'debmaster',
    visa = 'visa',
    debvisa = 'debvisa',
    diners = 'diners',
    oca = 'oca',
    lider = 'lider',
    amex = 'amex',
    redpagos = 'redpagos',
    abitab = 'abitab',
}

export interface PaymentCardBrand {
    key: string
    name: string
    image: string
    icon: string
}

export function getPaymentCardBrand(key: PaymentCardBrandKey): PaymentCardBrand {
    const map: Record<PaymentCardBrandKey, PaymentCardBrand> = {
        mp_account_money: {
            key: PaymentCardBrandKey.mp_account_money,
            name: 'Dinero en cuenta Mercado Pago',
            image: 'https://assets.retai.la/payments/mercadopago.png',
            icon: 'https://assets.retai.la/payments/mercadopago-ico.png',
        },
        master: {
            key: PaymentCardBrandKey.master,
            name: 'Mastercard',
            image: 'https://assets.retai.la/payments/master.png',
            icon: 'https://assets.retai.la/payments/master-ico.png',
        },
        debmaster: {
            key: PaymentCardBrandKey.master,
            name: 'Mastercard Débito',
            image: 'https://assets.retai.la/payments/master.png',
            icon: 'https://assets.retai.la/payments/master-ico.png',
        },
        visa: {
            key: PaymentCardBrandKey.visa,
            name: 'Visa',
            image: 'https://assets.retai.la/payments/visa.png',
            icon: 'https://assets.retai.la/payments/visa-ico.png',
        },
        debvisa: {
            key: PaymentCardBrandKey.visa,
            name: 'Visa Débito',
            image: 'https://assets.retai.la/payments/visa.png',
            icon: 'https://assets.retai.la/payments/visa-ico.png',
        },
        diners: {
            key: PaymentCardBrandKey.diners,
            name: 'Diners',
            image: 'https://assets.retai.la/payments/diners.png',
            icon: 'https://assets.retai.la/payments/diners-ico.png',
        },
        oca: {
            key: PaymentCardBrandKey.oca,
            name: 'Oca',
            image: 'https://assets.retai.la/payments/oca.png',
            icon: 'https://assets.retai.la/payments/oca-ico.png',
        },
        lider: {
            key: PaymentCardBrandKey.lider,
            name: 'Lider',
            image: 'https://assets.retai.la/payments/lider.png',
            icon: 'https://assets.retai.la/payments/lider-ico.png',
        },
        amex: {
            key: PaymentCardBrandKey.amex,
            name: 'American Express',
            image: 'https://assets.retai.la/payments/amex.png',
            icon: 'https://assets.retai.la/payments/amex-ico.png',
        },
        redpagos: {
            key: PaymentCardBrandKey.redpagos,
            name: 'Redpagos',
            image: 'https://assets.retai.la/payments/redpagos.png',
            icon: 'https://assets.retai.la/payments/redpagos-ico.png',
        },
        abitab: {
            key: PaymentCardBrandKey.abitab,
            name: 'Abitab',
            image: 'https://assets.retai.la/payments/abitab.png',
            icon: 'https://assets.retai.la/payments/abitab-ico.png',
        },
    }
    return (
        map[key] || {
            key,
            name: '',
            image: null,
        }
    )
}

export interface PaymentProviderAdapter {
    readonly key: PaymentProviderKey
    initPayment(input: PaymentProviderInitInput): Promise<PaymentProviderInitOutput>
    capture(input: PaymentProviderCaptureInput): Promise<PaymentProviderCaptureOutput>
    refund(input: PaymentProviderRefundInput): Promise<PaymentProviderRefundOutput>
    processWebhook(input: WebhookPayload['payload']): Promise<PaymentProviderWebhookResult>
}
